import React, { useContext, useEffect, useReducer, useMemo } from "react";
import shopify from "shopify-buy/index.unoptimized.umd";
import { useLocalStorage } from "react-use";

const ShopifyContext = React.createContext({});
const { Provider } = ShopifyContext;

const persistedStateId = "shopifyCheckout";

function createShopifyClient() {
  return shopify.buildClient({
    domain: `minibrew.myshopify.com`,
    storefrontAccessToken: "f73b90203be48fb50b7122b3ee872037",
    apiVersion: "2022-01",
  });
}

const shopifyActions = {
  setLoading: "set_loading",
  setCheckout: "set_checkout",
};

function shopifyCheckoutReducer(_, action) {
  switch (action.type) {
    case shopifyActions.setLoading:
      return { loaded: false };
    case shopifyActions.setCheckout:
      const { lineItems = [], subtotalPrice = 0, webUrl = "" } = action.payload;
      return { lineItems, subtotalPrice, webUrl, loaded: true };
    default:
      throw new Error(`Action of type ${action.type} does not exist.`);
  }
}

const useShopify = () => {
  const context = useContext(ShopifyContext);
  if (context === undefined) {
    throw new Error("ShopifyContext must be used within a ShopifyProvider");
  }
  return context;
};

const ShopifyProvider = ({ children }) => {
  const client = useMemo(() => createShopifyClient(), []);

  const [shopifyCheckoutId, setShopifyCheckoutId] = useLocalStorage(
    persistedStateId,
    ""
  );
  const [checkout, dispatch] = useReducer(shopifyCheckoutReducer, {
    loaded: false,
    subtotalPrice: 0,
    lineItems: [],
    webUrl: "",
  });

  async function addItem({ variantId, quantity }) {
    const temporalCheckout = await client.checkout.addLineItems(
      shopifyCheckoutId,
      [{ variantId, quantity }]
    );
    dispatch({
      type: shopifyActions.setCheckout,
      payload: temporalCheckout,
    });
  }

  async function removeItem(variantId) {
    const temporalCheckout = await client.checkout.removeLineItems(
      shopifyCheckoutId,
      [variantId]
    );

    dispatch({
      type: shopifyActions.setCheckout,
      payload: temporalCheckout,
    });
  }

  function getAvailableForSale(handle) {
    return client.product.fetchByHandle(handle).then((product) => {
      return product ? product.availableForSale : false;
    });
  }

  function getCollectionByHandle(handle) {
    return client.collection.fetchByHandle(handle).then((collection) => {
      return collection ? collection : [];
    });
  }

  function resetCart() {
    setShopifyCheckoutId("");

    dispatch({
      type: shopifyActions.setLoading,
    });
  }

  async function updateItem({ id, quantity }) {
    const temporalCheckout = await client.checkout.updateLineItems(
      shopifyCheckoutId,
      [
        {
          id,
          quantity,
        },
      ]
    );

    dispatch({
      type: shopifyActions.setCheckout,
      payload: temporalCheckout,
    });
  }

  const getQuantityByHandle = (handle) => {
    const qtyQuery = client.graphQLClient.query((root) => {
      root.add(
        "productByHandle",
        { args: { handle: `${handle}` } },
        (product) => {
          product.add("title");
          product.add("handle");
          product.add("id");
          product.addConnection(
            "variants",
            { args: { first: 99 } },
            (variant) => {
              variant.add("id");
              variant.add("availableForSale");
              variant.add("quantityAvailable");
            }
          );
        }
      );
    });
    return client.graphQLClient
      .send(qtyQuery)
      .then((res) => JSON.parse(JSON.stringify(res.model.productByHandle)))
      .catch(() => null);
  };

  useEffect(() => {
    if (!client || !client.checkout) return;

    async function createNewCheckout() {
      const checkout = await client.checkout.create();
      setShopifyCheckoutId(checkout.id);
      return checkout;
    }

    async function checkCartExistance() {
      let temporalCheckout = null;
      if (shopifyCheckoutId === "") {
        temporalCheckout = createNewCheckout();
      } else {
        temporalCheckout = await client.checkout.fetch(shopifyCheckoutId);
        if (temporalCheckout === null) {
          temporalCheckout = createNewCheckout();
        } else if (temporalCheckout.completedAt) {
          // If the saved checkout is already completed
          // create a new checkout
          temporalCheckout = createNewCheckout();
        }
      }

      dispatch({
        type: shopifyActions.setCheckout,
        payload: temporalCheckout,
      });
    }

    checkCartExistance();
  }, [shopifyCheckoutId, setShopifyCheckoutId, client]);

  return (
    <Provider
      value={{
        addItem,
        removeItem,
        resetCart,
        updateItem,
        checkout,
        getAvailableForSale,
        getQuantityByHandle,
        getCollectionByHandle,
        client,
      }}
    >
      {children}
    </Provider>
  );
};

export { ShopifyProvider, useShopify };
